<!-- components/form/InputShell.vue -->
<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

type Density = 'compact' | 'comfortable' | 'default'

const props = withDefaults(
  defineProps<{
    label?: string
    required?: boolean
    error?: string | string[] | null
    hint?: string
    density?: Density
    forId?: string
    hideAsterisk?: boolean
    disabled?: boolean
    readonly?: boolean
    translateErrors?: boolean
  }>(),
  {
    label: '',
    error: null,
    hint: '',
    forId: '',
    density: 'comfortable',
    hideAsterisk: false,
    translateErrors: true,
  }
)

const { t } = useI18n({ useScope: 'global' })

const rawErrors = computed<string[]>(() => {
  if (!props.error) return []
  return Array.isArray(props.error)
    ? props.error.filter(Boolean)
    : [props.error]
})

const errors = computed<string[]>(() => {
  if (!props.translateErrors) return rawErrors.value
  return rawErrors.value.map((e) => (typeof e === 'string' ? t(e) : String(e)))
})

const densityClass = computed(() =>
  props.density === 'compact'
    ? 'gap-1'
    : props.density === 'comfortable'
      ? 'gap-1.5'
      : 'gap-2'
)

const describedById = computed(() => {
  if (errors.value.length > 0) return `${props.forId || 'field'}-error`
  if (props.hint) return `${props.forId || 'field'}-hint`
  return undefined
})
</script>

<template>
  <div
    class="flex flex-col"
    :class="densityClass"
    :aria-disabled="disabled || undefined"
    :aria-readonly="readonly || undefined"
  >
    <!-- Label (externo) -->
    <div v-if="label || $slots['label-append']" class="flex items-center gap-2">
      <label
        v-if="label"
        :for="forId || undefined"
        class="text-sm font-medium text-gray-800"
      >
        {{ label }}
        <span
          v-if="required && !hideAsterisk"
          class="text-red-600"
          aria-hidden="true"
          >*</span
        >
      </label>

      <!-- Área para ícones/badges ao lado do label -->
      <slot name="label-append" />
    </div>

    <!-- Controle real (input/select/etc) -->
    <div class="w-full">
      <slot name="control" />
    </div>

    <!-- Erros -->
    <div
      v-if="errors.length"
      :id="describedById"
      class="text-xs leading-snug text-red-600"
      role="alert"
    >
      <!-- Slot opcional para personalizar bloco de erro inteiro -->
      <slot name="error">
        <ul class="m-0 list-none space-y-0.5 p-0">
          <li v-for="(e, i) in errors" :key="i">{{ e }}</li>
        </ul>
      </slot>
    </div>

    <!-- Hint (só aparece quando não há erro) -->
    <div
      v-else-if="hint || $slots.hint"
      :id="describedById"
      class="text-xs leading-snug text-gray-500"
    >
      <slot name="hint">
        {{ hint }}
      </slot>
    </div>

    <!-- Área opcional para ações abaixo do campo (ex.: links, botões auxiliares) -->
    <div v-if="$slots.actions" class="pt-1">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped>
/* estilos mínimos para quem não usa utilitários; pode remover se usa Tailwind  */
.text-gray-800 {
  color: #1f2937;
}
.text-gray-500 {
  color: #6b7280;
}
.text-red-600 {
  color: #dc2626;
}
</style>
